import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCart, k } from '../../context/CartContext';
import { img, taka, num } from './shopUtils';

const tid = (i) => (i.personalisation || i.gift_message ? `${i.variant_sku}-${[...((i.personalisation || '') + '|' + (i.gift_message || ''))].reduce((h, ch) => (Math.imul(h, 31) + ch.codePointAt(0)) >>> 0, 7).toString(36)}` : i.variant_sku);

export const CartLines = ({ compact = false }) => {
  const { language, t } = useLanguage();
  const cart = useCart();
  if (cart.items.length === 0) return <p className="py-10 text-center text-[#6B7280]" data-testid="cart-empty">{t({ en: 'Your cart is empty.', bn: 'আপনার কার্ট খালি।' })}</p>;
  return (
    <ul className="divide-y divide-[#E5E7EB]" data-testid="cart-lines">
      {cart.items.map((i) => (
        <li key={k(i)} className="flex gap-3 py-4" data-testid={`cart-line-${tid(i)}`}>
          <img src={img(i.image)} alt={i.name.en} className="h-20 w-24 shrink-0 rounded-md bg-[#F3F0EA] object-cover" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-[#0A0A0A]">{language === 'bn' ? i.name.bn || i.name.en : i.name.en}</p>
            <p className="text-xs text-[#6B7280]">{i.colour} · {i.size}</p>
            {i.personalisation && <p className="text-xs text-[#6B7280]" data-testid={`cart-personalisation-${tid(i)}`}>{t({ en: 'Name', bn: 'নাম' })}: {i.personalisation}</p>}
            {i.gift_message && <p className="truncate text-xs italic text-[#6B7280]">“{i.gift_message}”</p>}
            <div className="mt-2 flex items-center gap-3">
              <div className="flex items-center rounded-md border border-[#E5E7EB]">
                <button onClick={() => cart.setQty(k(i), i.qty - 1)} className="p-1.5" aria-label="Decrease" data-testid={`cart-dec-${tid(i)}`}><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-8 text-center text-sm" data-testid={`cart-qty-${tid(i)}`}>{num(i.qty, language)}</span>
                <button onClick={() => cart.setQty(k(i), i.qty + 1)} className="p-1.5" aria-label="Increase" data-testid={`cart-inc-${tid(i)}`}><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <button onClick={() => cart.remove(k(i))} className="text-[#6B7280] hover:text-[#E31E24]" aria-label="Remove" data-testid={`cart-remove-${tid(i)}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {!compact && <p className="font-semibold">{taka(i.price * i.qty, language)}</p>}
          {compact && <p className="text-sm font-semibold">{taka(i.price * i.qty, language)}</p>}
        </li>
      ))}
    </ul>
  );
};

export const CartDrawer = () => {
  const { language, t } = useLanguage();
  const cart = useCart();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!cart.open) return undefined;
    const onKey = (e) => e.key === 'Escape' && cart.setOpen(false);
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [cart.open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart.open) return null;
  const go = (path) => { cart.setOpen(false); navigate(path); };
  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={() => cart.setOpen(false)} />
      <aside ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Cart" className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl outline-none">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-4">
          <h2 className="shop-heading flex items-center gap-2 text-lg"><ShoppingBag className="h-5 w-5" /> {t({ en: 'Your cart', bn: 'আপনার কার্ট' })} ({num(cart.count, language)})</h2>
          <button onClick={() => cart.setOpen(false)} aria-label="Close cart" data-testid="cart-drawer-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4"><CartLines compact /></div>
        {cart.items.length > 0 && (
          <div className="space-y-3 border-t border-[#E5E7EB] p-4">
            <div className="flex justify-between font-semibold"><span>{t({ en: 'Subtotal', bn: 'সাবটোটাল' })}</span><span data-testid="cart-subtotal">{taka(cart.subtotal, language)}</span></div>
            <p className="text-xs text-[#6B7280]">{t({ en: 'Delivery and coupons are calculated at checkout. Free delivery in Dhaka over ৳3,000.', bn: 'ডেলিভারি ও কুপন চেকআউটে হিসাব হবে। ঢাকায় ৳৩,০০০-এর বেশি অর্ডারে ফ্রি ডেলিভারি।' })}</p>
            <button onClick={() => go('/checkout')} className="shop-btn w-full bg-[#E31E24] py-3 text-white hover:bg-[#c8181d]" data-testid="cart-checkout-btn">{t({ en: 'Checkout', bn: 'চেকআউট' })}</button>
            <button onClick={() => go('/cart')} className="shop-btn w-full border border-[#E5E7EB]" data-testid="cart-view-full-btn">{t({ en: 'View cart', bn: 'কার্ট দেখুন' })}</button>
          </div>
        )}
      </aside>
    </div>
  );
};
